'use client';

/**
 * Wiki Repo Sync Card Section
 *
 * Headline "Repo sync" row in the wiki settings dialog: an enable switch with
 * a confirmation popover, because enabling opens a PR in the user's repo.
 */
import { c as _c } from "react/compiler-runtime";
import type { useLiveWikiSettings } from '@/data/wiki/use-wiki-settings';
import type { WikiSettingResponse } from '@/lib/wiki-settings/schema';
import { HelpCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { BetterTooltip } from '@/components/ui/tooltip';
import { DEFAULT_WIKI_REPO_SYNC_PATH } from '@/lib/wiki/repo-sync/path';
type WikiRepoSyncCardSectionProps = {
  settings: WikiSettingResponse | null | undefined;
  installationId: number | null;
  repositoryId: number | null;
  isLoading: boolean;
  isPersisting: boolean;
  hasAutoRefreshEntitlement: boolean;
  onUpgradeRequired: () => void;
  updateSettings: ReturnType<typeof useLiveWikiSettings>['updateSettings'];
};
export function WikiRepoSyncCardSection(t0) {
  const $ = _c(45);
  const {
    settings,
    installationId,
    repositoryId,
    isLoading,
    isPersisting,
    hasAutoRefreshEntitlement,
    onUpgradeRequired,
    updateSettings
  } = t0;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pathInput, setPathInput] = useState(DEFAULT_WIKI_REPO_SYNC_PATH);
  const [isEnabling, setIsEnabling] = useState(false);
  const repoSyncAvailable = settings?.repoSyncAvailable ?? false;
  const repoSyncEnabled = repoSyncAvailable && (settings?.repoSyncEnabled ?? false);
  let t1;
  if ($[0] !== hasAutoRefreshEntitlement || $[1] !== installationId || $[2] !== onUpgradeRequired || $[3] !== repositoryId || $[4] !== settings?.repoSyncPath || $[5] !== updateSettings) {
    t1 = checked => {
      if (!installationId || !repositoryId) {
        return;
      }
      if (!checked) {
        updateSettings({
          repoSyncEnabled: false
        });
        return;
      }
      if (!hasAutoRefreshEntitlement) {
        onUpgradeRequired();
        return;
      }
      setPathInput(settings?.repoSyncPath ?? DEFAULT_WIKI_REPO_SYNC_PATH);
      setIsConfirmOpen(true);
    };
    $[0] = hasAutoRefreshEntitlement;
    $[1] = installationId;
    $[2] = onUpgradeRequired;
    $[3] = repositoryId;
    $[4] = settings?.repoSyncPath;
    $[5] = updateSettings;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleToggle = t1;
  let t2;
  if ($[7] !== pathInput || $[8] !== updateSettings) {
    t2 = () => {
      const transaction = updateSettings({
        repoSyncEnabled: true,
        repoSyncPath: pathInput.trim() || DEFAULT_WIKI_REPO_SYNC_PATH
      });
      if (!transaction) {
        setIsConfirmOpen(false);
        return;
      }
      setIsEnabling(true);
      transaction.isPersisted.promise.then(() => setIsConfirmOpen(false)).catch(_temp).finally(() => setIsEnabling(false));
    };
    $[7] = pathInput;
    $[8] = updateSettings;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleEnable = t2;
  if (!repoSyncAvailable) {
    return null;
  }
  let t3;
  if ($[10] !== isEnabling) {
    t3 = open => {
      if (!open && isEnabling) {
        return;
      }
      setIsConfirmOpen(open);
    };
    $[10] = isEnabling;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label htmlFor="repo-sync-enabled" className="font-medium text-sm text-muted-foreground">Repo sync</Label>;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="inline-flex items-center gap-1">{t4}<BetterTooltip content="Exports the wiki as markdown files into a folder in your repo, kept up to date through a rolling pull request."><HelpCircle className="h-3 w-3 text-muted-foreground/60 cursor-help" /></BetterTooltip></div>;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleToggle || $[15] !== isLoading || $[16] !== isPersisting || $[17] !== repoSyncEnabled) {
    t6 = <div className="flex items-center justify-between">{t5}{isLoading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <PopoverAnchor asChild={true}><span className="inline-flex"><Switch id="repo-sync-enabled" checked={repoSyncEnabled} onCheckedChange={handleToggle} disabled={isPersisting} /></span></PopoverAnchor>}</div>;
    $[14] = handleToggle;
    $[15] = isLoading;
    $[16] = isPersisting;
    $[17] = repoSyncEnabled;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="space-y-1"><p className="text-sm font-medium">Sync wiki to repository</p><p className="text-xs text-muted-foreground">Opens a pull request adding the wiki as markdown files to your repository, then keeps it updated after each refresh.</p></div>;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label htmlFor="repo-sync-confirm-path">Directory</Label>;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event => setPathInput(event.target.value);
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== isEnabling || $[23] !== pathInput) {
    t10 = <div className="space-y-2">{t8}<Input id="repo-sync-confirm-path" value={pathInput} onChange={t9} placeholder={DEFAULT_WIKI_REPO_SYNC_PATH} disabled={isEnabling} /></div>;
    $[22] = isEnabling;
    $[23] = pathInput;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsConfirmOpen(false);
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== isEnabling) {
    t12 = <Button type="button" variant="outline" size="sm" onClick={t11} disabled={isEnabling}>Cancel</Button>;
    $[26] = isEnabling;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isEnabling) {
    t13 = isEnabling ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null;
    $[28] = isEnabling;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== handleEnable || $[31] !== isEnabling || $[32] !== t13) {
    t14 = <Button type="button" size="sm" onClick={handleEnable} disabled={isEnabling}>{t13}Enable</Button>;
    $[30] = handleEnable;
    $[31] = isEnabling;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <div className="flex justify-end gap-2">{t12}{t14}</div>;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t10 || $[38] !== t15) {
    t16 = <PopoverContent align="end" className="w-72 space-y-3">{t7}{t10}{t15}</PopoverContent>;
    $[37] = t10;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== isConfirmOpen || $[41] !== t16 || $[42] !== t3 || $[43] !== t6) {
    t17 = <div className="space-y-2"><Popover open={isConfirmOpen} onOpenChange={t3}>{t6}{t16}</Popover></div>;
    $[40] = isConfirmOpen;
    $[41] = t16;
    $[42] = t3;
    $[43] = t6;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
}
function _temp() {}